import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { BuildingType, BuildingZone, Planet, PlayerRace } from '@kaeliux/shared';
import { cn } from '../../utils/cn';
import { observedBorderBoxHeight } from '../../utils/observedBorderBoxHeight';
import { useNow } from '../../hooks/useNow';
import { useAmbientAnimations } from '../../hooks/useAmbientAnimations';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion';
import { pendingBuilds, groupPendingBuildsByBuilding, groupPendingBuildsByPlot } from '../../utils/buildQueue';
import { useUniverseStore } from '../../store/universeStore';
import { usePlanetView } from '../../contexts/PlanetViewContext';
import { computeProjectedBuildingLevels } from '@kaeliux/game-engine';
import type { PlanetActionOptions } from '../../services/planet.service';
import { findPlotOccupant, surfacePropAreas, SURFACE_LAYOUT } from './surfaceLayout';
import { BuildingTile, type BuildingUpgradeProgress } from './BuildingTile';
import { PlotTile } from './PlotTile';
import { ZoneStrip } from './ZoneStrip';
import { BuildingPopover } from './BuildingPopover';
import { SurfaceListViewLink } from './SurfaceListViewLink';
import { ZONE_LABEL, ZONE_TEXT_CLASS, ZONE_BG_CLASS } from './zoneStyles';
import {
    ZONE_ORDER,
    ZONE_WIDTH,
    WORLD_WIDTH,
    ARROW_STEP_PX,
    bannerAnchor,
    bannerWidth,
    clampScroll,
    decayVelocity,
    hashSeed,
    parallaxAnchor,
    parallaxOffset,
    parallaxSpan,
    pickSkyAsteroid,
    projectTile,
    propDepthScale,
    scatterRocks,
    scatterSurfaceProps,
    scatterTerrainPatches,
    sceneGeometry,
    scrollForZone,
    starFieldShadow,
    stepToward,
    zoneIndexAt,
} from '../../utils/surfaceScene';
import { SURFACE_PROP_ASSETS, SURFACE_PROP_KINDS_BY_ZONE } from '../../utils/surfaceProps';
import groundTextureUrl from '../../assets/images/surface/ground-texture.jpg';
import mountainsFarUrl from '../../assets/images/surface/mountains-far.svg';
import mountainsMidUrl from '../../assets/images/surface/mountains-mid.svg';
// Reused for two decorative purposes below (DS-29 issue #227 round 3): real
// rock photos on the ground (`GROUND_ROCK_IMAGES`) and one random tiny
// asteroid in the sky (`SKY_ASTEROID_IMAGES`) — same photographic asteroid
// renders the galaxy map already uses (`GalaxyPage.tsx`), not new assets.
import asteroid1 from '../../assets/images/galaxy/asteroid1.png';
import asteroid2 from '../../assets/images/galaxy/asteroid2.png';
import asteroid3 from '../../assets/images/galaxy/asteroid3.png';
import asteroid4 from '../../assets/images/galaxy/asteroid4.png';
import asteroid5 from '../../assets/images/galaxy/asteroid5.png';
import asteroid6 from '../../assets/images/galaxy/asteroid6.png';

const DRAG_THRESHOLD_PX = 6;
const RESIZE_DEBOUNCE_MS = 120;
const VELOCITY_CLAMP = 70;
/**
 * A release only carries momentum if the pointer was still moving. The
 * recorded velocity comes from the last pointermove, so without this the
 * sequence "drag fast, hold still, release" flings the world on its own.
 */
const VELOCITY_STALE_MS = 90;
const STAR_TILE_WIDTH = 1600;

const STARS_FACTOR = 0.08;
const SKY_OBJECTS_FACTOR = 0.16;
/** Parallax factor of the zone-banner layer. Exported for tests. */
export const ZONE_BANNER_FACTOR = 0.72;
const FAR_RIDGE_FACTOR = 0.3;
const MID_RIDGE_FACTOR = 0.55;
const ROCKS_FACTOR = 1;
const ZONE_EDGE_FACTOR = 1;

const ZONE_TAGLINE: Record<BuildingZone, string> = {
    resource: 'Extraction Belt',
    industry: 'Forge Flats',
    military: 'Command Ridge',
};

const ZONE_WASH_COLOR: Record<BuildingZone, string> = {
    resource: 'oklch(80% 0.09 205 / 7%)',
    industry: 'oklch(82% 0.11 78 / 7%)',
    military: 'oklch(66% 0.17 18 / 7%)',
};

const SKY_GRADIENT =
    'linear-gradient(180deg, #05050f 0%, #0a0a1e 34%, #141232 62%, #241a3a 82%, #3a2440 100%)';
/**
 * DS-29 issue #227 round 2: "terrain is still flat with nothing on the
 * horizon" — these ridges already existed (DS-13) but read as invisible:
 * their layer only extended 60px/30px above `groundTop` (see the `top`
 * values below), and that thin sliver's colour sat too close to the sky
 * gradient's own near-horizon stop (`#3a2440`) to read as a silhouette.
 * Darkened here so the ridge reads as a solid skyline backlit by the
 * horizon glow, rather than blending into the sky behind it.
 */
/**
 * DS-29 issue #227: the ridges are hand-built low-poly art now
 * (`assets/images/surface/mountains-*.svg`), not the procedural triangle strip
 * this used to generate. Each mount in those files is drawn the way the
 * reference art is — a peak, two kinked outer ridges running down to a shared
 * flat base line, inner ridges at differing angles between them, and facets
 * that step lighter-to-darker left to right for a single light direction.
 *
 * Both files end exactly on their base line, so a layer whose bottom edge sits
 * at `groundTop` puts the mountains' feet on the horizon with nothing to hide
 * under the ground div. Far ridge is the taller, lighter, backlit-by-the-glow
 * range; mid ridge is the lower, darker foothills in front of it — the same
 * relative relationship the old generated ridges had.
 */
const FAR_RIDGE_HEIGHT_PX = 260;
const MID_RIDGE_HEIGHT_PX = 150;
/**
 * Foreground (bottom, near the camera) lighter than the horizon (top) — DS-29
 * issue #227: this used to run the other way, dark at the front and light at
 * the back, which read backwards against every light-from-above scene the
 * rest of the surface implies. Stops mirrored, not just recoloured.
 */
const GROUND_GRADIENT = 'linear-gradient(180deg,#12101f,#1d1830 62%,#2a2340)';
const HORIZON_GLOW_COLOR = 'oklch(60% 0.1 300 / 22%)';

/**
 * DS-29 issue #227: the ground rocks are low-poly chips from the same hand-built
 * art as the mountain ridges, not the asteroid photos they used to reuse — a
 * photographic rock next to flat-shaded scenery read as a sticker. Discovered
 * with `import.meta.glob` rather than a hand-kept import list, the same way
 * `surfaceProps.ts` picks up per-race props: drop a file in the folder and it
 * joins the pool. `SKY_ASTEROID_IMAGES` below still uses the photos, for the
 * unrelated sky object.
 */
const CHIP_MODULES = import.meta.glob<string>('../../assets/images/surface/chips/*.svg', {
    eager: true,
    import: 'default',
});
const GROUND_CHIP_IMAGES = Object.keys(CHIP_MODULES)
    .sort()
    .map((path) => CHIP_MODULES[path]);
const SKY_ASTEROID_IMAGES = [asteroid1, asteroid2, asteroid3, asteroid4, asteroid5, asteroid6];
/**
 * `scatterRocks` sizes a rock in the same 6-24px range it used for the old
 * asteroid photos, where the whole sprite was the rock. A chip is drawn with
 * its own margins inside a wider-than-tall viewBox, so at that raw width it
 * renders as a speck; this scales the range up to roughly 16-62px on screen,
 * which reads as ground clutter without competing with a prop.
 */
const CHIP_WIDTH_SCALE = 2.6;
const ROCK_SEED = 19;
/** Capped at the user's explicit ask, round 3: "5 will be maximum number of rocks on the terrain." */
const ROCK_COUNT = 5;
/**
 * DS-29: ground texture + scattered, race-styled props, seeded off the
 * planet id (not a fixed constant like ROCK_SEED) so a given planet always
 * looks the same but different planets don't look identical — see
 * `hashSeed`/`scatterSurfaceProps` in surfaceScene.ts. Prop art itself is
 * per-race (`SURFACE_PROP_ASSETS`/`SURFACE_PROP_KINDS_BY_ZONE` in
 * utils/surfaceProps.ts) — no tint filter needed, it's already drawn to
 * match each race's own building palette.
 *
 * DS-29 issue #227 round 5: "we have 16 prop sprites, show each at most
 * once — I see 6-7." The scatter places at most one of each kind and never
 * repeats one.
 *
 * DS-29 issue #227 round 9: the count is no longer a race sprite-pool size
 * either — it's however many of `surfacePropAreas`' 12 curated pockets end
 * up filled, since round 9 replaced the free-scatter-plus-avoid-rects
 * search with fixed placements the user hand-picked relative to specific
 * plots (see `scatterSurfaceProps`'s doc comment in surfaceScene.ts).
 */
/**
 * Rendered height for a scale=1, horizon-depth prop, before the scatter's
 * own 0.7-1.3 multiplier and `propDepthScale`'s foreground/background factor
 * (DS-29 issue #227: without the latter, a prop right at the camera read no
 * bigger than one near the horizon, tiny next to a foreground building that
 * itself scales up). Even a foreground prop at its biggest tops out well
 * under a building's own render size (BuildingTile's sprite caps at 194px
 * tall) — props are background clutter, not competing set-pieces. Width is
 * left to the browser (`auto`) so each sprite keeps its own aspect ratio; no
 * per-file size bookkeeping needed as the race folders gain/lose sprites.
 */
const PROP_TARGET_HEIGHT_PX = 70;
const GROUND_TEXTURE_TILE_PX = 220;
const TERRAIN_PATCH_SEED = 67;
/**
 * DS-29 issue #227: the ground read as a flat gradient with no relief. These
 * are not real geometry — see `scatterTerrainPatches` — just soft, irregular
 * dark blobs faking the shading unevenness would cast.
 */
const TERRAIN_PATCH_COUNT = 34;
const STAR_SEED_A = 7;
const STAR_SEED_B = 41;
const STAR_COUNT = 90;
const STAR_HEIGHT = 480;

interface SkyObject {
    key: string;
    worldX: number;
    depth: number;
    size: number;
    color: string;
}

const SKY_OBJECTS: SkyObject[] = [
    { key: 'planet-1', worldX: WORLD_WIDTH * 0.14, depth: 0.85, size: 120, color: '#7c5295' },
    { key: 'planet-2', worldX: WORLD_WIDTH * 0.62, depth: 0.7, size: 90, color: '#aa96da' },
];

/**
 * DS-29 issue #227 round 3: "add one random tiny asteroid (from existing
 * images) in the sky" — a real photo (`SKY_ASTEROID_IMAGES`, above) instead
 * of a flat CSS circle, seeded off the planet id like the surface props so a
 * given planet always shows the same one but different planets don't match.
 * XORed with a salt so this doesn't draw the exact same RNG sequence
 * `scatterSurfaceProps` already derives from the same planet id.
 */
const SKY_ASTEROID_SEED_SALT = 0x5eed;
const SKY_ASTEROID_DEPTH = 0.92;
/** Base size before `projectTile`'s depth scale shrinks it further — "tiny" is the ask. */
const SKY_ASTEROID_BASE_SIZE_PX = 26;

function zoneBoundaries(): number[] {
    const boundaries: number[] = [];
    for (let i = 1; i < ZONE_ORDER.length; i++) {
        boundaries.push(i * ZONE_WIDTH);
    }
    return boundaries;
}

const ZONE_BOUNDARIES = zoneBoundaries();

export interface PlanetSurfaceProps {
    /** Building levels + pending upgrades come from here (DS-15). Omit/null renders every slot as level 0, no upgrades. */
    planet?: Planet | null;
    /** Player's race, for per-race sprite resolution. Falls back to humans (see `getBuildingIcon`) when omitted. */
    race?: PlayerRace;
    /**
     * The fixed bottom dock's measured height (DS-11's `PlanetNavigation`,
     * measured by its caller via `observedBorderBoxHeight` same as
     * `AppShell` does for its own nav spacer). The zone strip (DS-16) sits
     * this many px above the bottom of the stage rather than a magic
     * number, so it never overlaps the dock. Defaults to 0 for callers
     * with no dock mounted alongside it (e.g. a unit test).
     */
    dockHeight?: number;
}

/**
 * Parallax stage for the planet surface (DS-14) plus its building tiles
 * (DS-15). Scenery positioning math is `surfaceScene.ts` (DS-13); this
 * component measures the viewport, tracks scroll/drag/momentum state, and
 * applies `translateX` to a fixed set of layer wrappers, including the
 * tiles layer built from `surfaceLayout.ts`.
 */
export function PlanetSurface({ planet = null, race, dockHeight = 0 }: PlanetSurfaceProps) {
    const stageRef = useRef<HTMLDivElement | null>(null);
    const [viewportWidth, setViewportWidth] = useState(0);
    const [viewportHeight, setViewportHeight] = useState(0);
    const [scrollX, setScrollX] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [openTileId, setOpenTileId] = useState<string | null>(null);

    const prefersReducedMotion = usePrefersReducedMotion();
    const ambientAnimations = useAmbientAnimations();

    const view = usePlanetView();
    const { resourceMultiplier, speedMultiplier, sunAges } = useUniverseStore();
    const scienceLevels = view.scienceLevels;
    const projectedLevels = useMemo(
        () => (planet ? computeProjectedBuildingLevels(planet, planet.tickCheckpoints ?? []) : ({} as Record<BuildingType, number>)),
        [planet],
    );
    const buildOptions: PlanetActionOptions = useMemo(
        () => ({ resourceMultiplier, sunAges, speedMultiplier }),
        [resourceMultiplier, sunAges, speedMultiplier],
    );
    const allPendingBuilds = useMemo(() => pendingBuilds(planet), [planet]);
    const queueFull = allPendingBuilds.length >= view.buildingQueueLimit;

    const hasMeasuredRef = useRef(false);
    const scrollXRef = useRef(0);
    const dragRef = useRef<{
        active: boolean;
        startClientX: number;
        startScroll: number;
        lastClientX: number;
        lastTime: number;
        velocity: number;
    } | null>(null);
    const velocityRef = useRef(0);
    const easeTargetRef = useRef<number | null>(null);
    const rafIdRef = useRef<number | null>(null);

    useEffect(() => {
        scrollXRef.current = scrollX;
    }, [scrollX]);

    // Measure the stage with a ResizeObserver, debounced ~120ms. First
    // measure centres the Resource zone; later resizes preserve the
    // current scroll position, clamped to the new bounds.
    useEffect(() => {
        const node = stageRef.current;
        if (!node) return undefined;

        let debounceId: ReturnType<typeof setTimeout> | null = null;

        const apply = (width: number, height: number) => {
            setViewportHeight(height);
            if (!hasMeasuredRef.current) {
                hasMeasuredRef.current = true;
                setViewportWidth(width);
                const centred = scrollForZone(0, width);
                scrollXRef.current = centred;
                setScrollX(centred);
                return;
            }
            setViewportWidth(width);
            const preserved = clampScroll(scrollXRef.current, width);
            scrollXRef.current = preserved;
            setScrollX(preserved);
        };

        const observer = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (!entry) return;
            const height = observedBorderBoxHeight(entry, node);
            const width = entry.borderBoxSize?.[0]?.inlineSize ?? node.offsetWidth;
            if (debounceId) clearTimeout(debounceId);
            debounceId = setTimeout(() => apply(width, height), RESIZE_DEBOUNCE_MS);
        });
        observer.observe(node);

        return () => {
            if (debounceId) clearTimeout(debounceId);
            observer.disconnect();
        };
    }, []);

    const stopRaf = useCallback(() => {
        if (rafIdRef.current !== null) {
            cancelAnimationFrame(rafIdRef.current);
            rafIdRef.current = null;
        }
    }, []);

    const runFrame = useCallback(() => {
        let stillAnimating = false;

        if (velocityRef.current !== 0) {
            const nextVelocity = decayVelocity(velocityRef.current);
            velocityRef.current = nextVelocity;
            const next = clampScroll(scrollXRef.current + nextVelocity, viewportWidth);
            scrollXRef.current = next;
            setScrollX(next);
            if (nextVelocity !== 0) stillAnimating = true;
        }

        if (easeTargetRef.current !== null) {
            const result = stepToward(scrollXRef.current, easeTargetRef.current);
            const next = clampScroll(result.value, viewportWidth);
            scrollXRef.current = next;
            setScrollX(next);
            if (result.done) {
                easeTargetRef.current = null;
            } else {
                stillAnimating = true;
            }
        }

        if (stillAnimating) {
            rafIdRef.current = requestAnimationFrame(runFrame);
        } else {
            rafIdRef.current = null;
        }
    }, [viewportWidth]);

    const ensureRafRunning = useCallback(() => {
        if (rafIdRef.current === null) {
            rafIdRef.current = requestAnimationFrame(runFrame);
        }
    }, [runFrame]);

    useEffect(() => stopRaf, [stopRaf]);

    const scrollTo = useCallback(
        (target: number) => {
            const clamped = clampScroll(target, viewportWidth);
            if (prefersReducedMotion) {
                easeTargetRef.current = null;
                scrollXRef.current = clamped;
                setScrollX(clamped);
                return;
            }
            easeTargetRef.current = clamped;
            ensureRafRunning();
        },
        [viewportWidth, prefersReducedMotion, ensureRafRunning],
    );

    /**
     * Deliberately does NOT call setPointerCapture. Capturing here retargets the
     * rest of the gesture at the stage, so the click that ends a tap never reaches
     * the element under the cursor and in-stage links stop navigating. The window
     * listeners below already carry pointermove/up for the whole drag, so capture
     * bought nothing. See planet-surface-pan.spec.ts.
     */
    const handlePointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
        if (event.button !== 0) return;
        easeTargetRef.current = null;
        velocityRef.current = 0;
        dragRef.current = {
            active: false,
            startClientX: event.clientX,
            startScroll: scrollXRef.current,
            lastClientX: event.clientX,
            lastTime: performance.now(),
            velocity: 0,
        };
    }, []);

    useEffect(() => {
        const handleMove = (event: PointerEvent) => {
            const drag = dragRef.current;
            if (!drag) return;

            const totalDelta = event.clientX - drag.startClientX;
            if (!drag.active) {
                if (Math.abs(totalDelta) < DRAG_THRESHOLD_PX) return;
                drag.active = true;
                setIsDragging(true);
                // Starting a drag closes any open popover immediately, per DS-17.
                setOpenTileId(null);
            }

            const now = performance.now();
            const dt = Math.max(1, now - drag.lastTime);
            const frameDelta = event.clientX - drag.lastClientX;
            // Momentum is added directly to scrollX each frame, and scrollX
            // moves opposite to clientX (`startScroll - totalDelta` above),
            // so the recorded velocity must be negated to continue in the
            // same direction the drag was already moving.
            drag.velocity = -(frameDelta / dt) * 16;
            drag.lastClientX = event.clientX;
            drag.lastTime = now;

            const next = clampScroll(drag.startScroll - totalDelta, viewportWidth);
            scrollXRef.current = next;
            setScrollX(next);
        };

        const handleUp = (event: PointerEvent) => {
            const drag = dragRef.current;
            if (!drag) return;
            dragRef.current = null;
            if (!drag.active) {
                // A tap (no drag past the threshold): hit-test for a tile
                // (DS-15's `data-tile-id`) and open its popover, or close
                // whatever's open when the tap landed on empty ground.
                const target = document.elementFromPoint(event.clientX, event.clientY);
                const tile = target?.closest<HTMLElement>('[data-tile-id]');
                setOpenTileId(tile?.dataset.tileId ?? null);
                return;
            }
            setIsDragging(false);

            const wentStill = performance.now() - drag.lastTime > VELOCITY_STALE_MS;

            if (!prefersReducedMotion && !wentStill) {
                const clamped = Math.min(Math.max(drag.velocity, -VELOCITY_CLAMP), VELOCITY_CLAMP);
                velocityRef.current = clamped;
                if (clamped !== 0) ensureRafRunning();
            }
        };

        // A browser that claims the gesture for its own scrolling fires
        // pointercancel and then stops sending moves. Without this the drag
        // state was left dangling and the world stopped following the finger
        // partway through the gesture.
        const handleCancel = () => {
            dragRef.current = null;
            setIsDragging(false);
        };

        window.addEventListener('pointermove', handleMove);
        window.addEventListener('pointerup', handleUp);
        window.addEventListener('pointercancel', handleCancel);
        return () => {
            window.removeEventListener('pointermove', handleMove);
            window.removeEventListener('pointerup', handleUp);
            window.removeEventListener('pointercancel', handleCancel);
        };
    }, [viewportWidth, prefersReducedMotion, ensureRafRunning]);

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            // Before the first ResizeObserver measurement the viewport reads 0,
            // which makes every clamp bound meaningless and strands the world
            // half a zone in from the left edge.
            if (viewportWidth <= 0) return;
            if (event.key === 'ArrowLeft') {
                event.preventDefault();
                const next = clampScroll(scrollXRef.current - ARROW_STEP_PX, viewportWidth);
                scrollXRef.current = next;
                setScrollX(next);
            } else if (event.key === 'ArrowRight') {
                event.preventDefault();
                const next = clampScroll(scrollXRef.current + ARROW_STEP_PX, viewportWidth);
                scrollXRef.current = next;
                setScrollX(next);
            } else if (event.key === 'Home') {
                event.preventDefault();
                scrollTo(scrollForZone(0, viewportWidth));
            } else if (event.key === 'End') {
                event.preventDefault();
                scrollTo(scrollForZone(ZONE_ORDER.length - 1, viewportWidth));
            }
        },
        [viewportWidth, scrollTo],
    );

    const now = useNow();
    const pendingBuildsByBuilding = useMemo(
        () => groupPendingBuildsByBuilding(allPendingBuilds),
        [allPendingBuilds],
    );
    // Only a level 0→1 construct's event carries a plotId (DS-28) — this is
    // how an empty plot finds its own in-progress construct, separately
    // from `pendingBuildsByBuilding`'s per-building groups for a built
    // tile's upgrade.
    const pendingBuildsByPlot = useMemo(
        () => groupPendingBuildsByPlot(allPendingBuilds),
        [allPendingBuilds],
    );

    const openSlot = useMemo(() => SURFACE_LAYOUT.find((slot) => slot.id === openTileId) ?? null, [openTileId]);
    const [anchorRect, setAnchorRect] = useState<{ left: number; top: number; width: number; height: number } | null>(null);

    useEffect(() => {
        if (!openTileId) {
            setAnchorRect(null);
            return;
        }
        const el = document.querySelector<HTMLElement>(`[data-tile-id="${openTileId}"]`);
        if (!el) {
            setAnchorRect(null);
            return;
        }
        const rect = el.getBoundingClientRect();
        setAnchorRect({ left: rect.left, top: rect.top, width: rect.width, height: rect.height });
        // Re-measure whenever the world scrolls (tiles move via translateX)
        // or the stage is resized, so the popover tracks its tile.
    }, [openTileId, scrollX, viewportWidth, viewportHeight]);

    const activeZoneIndex = zoneIndexAt(scrollX, viewportWidth);
    const activeZone = ZONE_ORDER[activeZoneIndex];

    const { groundTop, groundHeight } = sceneGeometry(viewportHeight);
    const rocks = useMemo(
        () => scatterRocks(ROCK_SEED, ROCK_COUNT, groundTop, groundHeight),
        [groundTop, groundHeight],
    );
    const effectiveRace: PlayerRace = race ?? 'humans';
    const propAreas = useMemo(() => surfacePropAreas(groundTop, groundHeight), [groundTop, groundHeight]);
    const surfaceProps = useMemo(
        () =>
            scatterSurfaceProps(
                hashSeed(planet?.id ?? 'surface-preview'),
                propAreas,
                groundTop,
                groundHeight,
                SURFACE_PROP_KINDS_BY_ZONE[effectiveRace],
                PROP_TARGET_HEIGHT_PX,
            ),
        [planet?.id, propAreas, groundTop, groundHeight, effectiveRace],
    );
    const terrainPatches = useMemo(
        () => scatterTerrainPatches(TERRAIN_PATCH_SEED, TERRAIN_PATCH_COUNT, groundTop, groundHeight),
        [groundTop, groundHeight],
    );
    const skyAsteroid = useMemo(
        () => pickSkyAsteroid(hashSeed(planet?.id ?? 'surface-preview') ^ SKY_ASTEROID_SEED_SALT, SKY_ASTEROID_IMAGES.length),
        [planet?.id],
    );
    const starShadowA = useMemo(
        () => starFieldShadow(STAR_SEED_A, STAR_COUNT, STAR_TILE_WIDTH, STAR_HEIGHT),
        [],
    );
    const starShadowB = useMemo(
        () => starFieldShadow(STAR_SEED_B, STAR_COUNT, STAR_TILE_WIDTH, STAR_HEIGHT),
        [],
    );

    const starsOffset = parallaxOffset(scrollX, STARS_FACTOR);
    const starsTranslate = -(((starsOffset % STAR_TILE_WIDTH) + STAR_TILE_WIDTH) % STAR_TILE_WIDTH);
    const skyObjectsOffset = -parallaxOffset(scrollX, SKY_OBJECTS_FACTOR);
    const bannersOffset = -parallaxOffset(scrollX, ZONE_BANNER_FACTOR);
    const farRidgeOffset = -parallaxOffset(scrollX, FAR_RIDGE_FACTOR);
    const midRidgeOffset = -parallaxOffset(scrollX, MID_RIDGE_FACTOR);
    const rocksOffset = -parallaxOffset(scrollX, ROCKS_FACTOR);
    const zoneEdgeOffset = -parallaxOffset(scrollX, ZONE_EDGE_FACTOR);

    const floatClass = ambientAnimations ? 'animate-float-slow' : '';
    const twinkleClass = ambientAnimations ? 'animate-twinkle' : '';

    return (
        <div
            ref={stageRef}
            role="region"
            aria-label="Planet surface"
            tabIndex={0}
            onPointerDown={handlePointerDown}
            onKeyDown={handleKeyDown}
            data-testid="planet-surface"
            className={cn(
                'relative h-full w-full overflow-hidden select-none outline-none',
                isDragging ? 'cursor-grabbing' : 'cursor-grab',
            )}
            // `pan-y` keeps vertical page scrolling with the browser while this
            // stage owns horizontal panning; without it the browser took over the
            // gesture and cancelled the drag a few pixels in.
            style={{ userSelect: 'none', touchAction: 'pan-y' }}
        >
            <div className="absolute inset-0" style={{ background: SKY_GRADIENT }} />

            <div
                data-testid="planet-surface-stars"
                className="absolute top-0 left-0"
                style={{ transform: `translateX(${starsTranslate}px)`, width: STAR_TILE_WIDTH * 2, height: STAR_HEIGHT }}
            >
                <div
                    className={cn('absolute top-0 left-0 h-px w-px rounded-full', twinkleClass)}
                    style={{ boxShadow: starShadowA }}
                />
                <div
                    className={cn('absolute top-0 h-px w-px rounded-full', twinkleClass)}
                    style={{ left: STAR_TILE_WIDTH, boxShadow: starShadowB }}
                />
            </div>

            <div
                data-testid="planet-surface-sky-objects"
                className="absolute inset-0"
                style={{ transform: `translateX(${skyObjectsOffset}px)` }}
            >
                {SKY_OBJECTS.map((object) => {
                    const projection = projectTile(object.depth, groundTop, groundHeight);
                    return (
                        <div
                            key={object.key}
                            className={cn('absolute rounded-full', floatClass)}
                            style={{
                                left: parallaxAnchor(object.worldX, SKY_OBJECTS_FACTOR, viewportWidth),
                                top: projection.top - projection.scale * object.size,
                                width: object.size * projection.scale,
                                height: object.size * projection.scale,
                                background: object.color,
                                opacity: projection.opacity,
                                zIndex: projection.zIndex,
                            }}
                        />
                    );
                })}
                {(() => {
                    const projection = projectTile(SKY_ASTEROID_DEPTH, groundTop, groundHeight);
                    const size = SKY_ASTEROID_BASE_SIZE_PX * projection.scale;
                    return (
                        <img
                            data-testid="planet-surface-sky-asteroid"
                            src={SKY_ASTEROID_IMAGES[skyAsteroid.imageIndex]}
                            alt=""
                            draggable={false}
                            className={cn('absolute select-none', floatClass)}
                            style={{
                                left: parallaxAnchor(skyAsteroid.worldX, SKY_OBJECTS_FACTOR, viewportWidth),
                                top: projection.top - size,
                                width: size,
                                height: size,
                                maxWidth: 'none',
                                opacity: projection.opacity,
                                zIndex: projection.zIndex,
                            }}
                        />
                    );
                })()}
            </div>

            <div
                data-testid="planet-surface-zone-banners"
                className="absolute inset-0"
                style={{ transform: `translateX(${bannersOffset}px)` }}
            >
                {ZONE_ORDER.map((zone, index) => {
                    const isActive = zone === activeZone;
                    return (
                        <div
                            key={zone}
                            data-testid={`zone-banner-${zone}`}
                            className="absolute top-[18%] flex flex-col items-center"
                            style={{
                                left: bannerAnchor(index, ZONE_BANNER_FACTOR, viewportWidth),
                                width: bannerWidth(viewportWidth),
                                opacity: isActive ? 1 : 0.45,
                            }}
                        >
                            <span
                                className={cn('text-[9vw] font-extrabold leading-none tracking-[0.16em] sm:text-banner', ZONE_TEXT_CLASS[zone])}
                                style={{ opacity: 0.16 }}
                            >
                                {ZONE_LABEL[zone]}
                            </span>
                            <span
                                className={cn('text-micro tracking-[0.3em] uppercase', ZONE_TEXT_CLASS[zone])}
                                style={{ opacity: 0.4 }}
                            >
                                {ZONE_TAGLINE[zone]}
                            </span>
                        </div>
                    );
                })}
            </div>

            <div
                data-testid="planet-surface-far-ridge"
                className="absolute inset-x-0"
                style={{
                    top: groundTop - FAR_RIDGE_HEIGHT_PX,
                    height: FAR_RIDGE_HEIGHT_PX,
                    width: parallaxSpan(FAR_RIDGE_FACTOR, viewportWidth),
                    transform: `translateX(${farRidgeOffset}px)`,
                }}
            >
                <img src={mountainsFarUrl} alt="" draggable={false} className="block h-full w-full select-none" style={{ objectFit: 'fill' }} />
            </div>

            <div
                data-testid="planet-surface-mid-ridge"
                className="absolute inset-x-0"
                style={{
                    top: groundTop - MID_RIDGE_HEIGHT_PX,
                    height: MID_RIDGE_HEIGHT_PX,
                    width: parallaxSpan(MID_RIDGE_FACTOR, viewportWidth),
                    transform: `translateX(${midRidgeOffset}px)`,
                }}
            >
                <img src={mountainsMidUrl} alt="" draggable={false} className="block h-full w-full select-none" style={{ objectFit: 'fill' }} />
            </div>

            <div
                data-testid="planet-surface-horizon-glow"
                className="absolute inset-x-0"
                style={{
                    top: groundTop - 7,
                    height: 14,
                    background: HORIZON_GLOW_COLOR,
                    filter: 'blur(5px)',
                }}
            />

            <div
                data-testid="planet-surface-ground"
                className="absolute inset-x-0 bottom-0"
                style={{ top: groundTop, background: GROUND_GRADIENT }}
            >
                <div
                    className="absolute inset-0"
                    style={{ background: 'radial-gradient(ellipse at 50% 100%, oklch(45% 0.08 300 / 20%), transparent 60%)' }}
                />
            </div>

            {/*
              DS-29 first pass: a tiling photo texture, not hundreds of DOM
              nodes — cheap on the drag-every-frame repaint budget this issue
              calls for. Same `rocksOffset` as the rocks/tiles layers below so
              the ground doesn't slide relative to what's standing on it.
              Low opacity + soft-light blend so it reads as ground detail
              under the zone wash, not a photograph pasted over the gradient.
            */}
            <div
                data-testid="planet-surface-ground-texture"
                className="pointer-events-none absolute inset-x-0 bottom-0"
                style={{
                    top: groundTop,
                    width: WORLD_WIDTH,
                    transform: `translateX(${rocksOffset}px)`,
                    backgroundImage: `url(${groundTextureUrl})`,
                    backgroundRepeat: 'repeat',
                    backgroundSize: `${GROUND_TEXTURE_TILE_PX}px ${GROUND_TEXTURE_TILE_PX}px`,
                    opacity: 0.5,
                    mixBlendMode: 'soft-light',
                }}
            />

            {/*
              DS-29 issue #227: fake terrain relief. Not real geometry — soft,
              irregularly-shaped (`border-radius`, not an ellipse) dark blobs
              low enough opacity to read as ground shading rather than
              objects. Sits under rocks/props/tiles, same `rocksOffset` so it
              doesn't slide relative to what's standing on it.
            */}
            <div
                data-testid="planet-surface-terrain-patches"
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 bottom-0"
                style={{ top: groundTop, width: WORLD_WIDTH, transform: `translateX(${rocksOffset}px)` }}
            >
                {terrainPatches.map((patch, index) => (
                    <div
                        key={index}
                        className="absolute"
                        style={{
                            left: `${patch.x}%`,
                            top: patch.y - groundTop - patch.height / 2,
                            width: patch.width,
                            height: patch.height,
                            transform: `translateX(-50%) rotate(${patch.rotation}deg)`,
                            borderRadius: patch.radii,
                            background: 'radial-gradient(ellipse at 50% 50%, rgba(0,0,0,0.6), rgba(0,0,0,0) 75%)',
                            filter: 'blur(6px)',
                            opacity: patch.opacity,
                        }}
                    />
                ))}
            </div>

            <div
                data-testid="planet-surface-rocks"
                className="absolute inset-x-0 bottom-0"
                style={{ top: groundTop, width: WORLD_WIDTH, transform: `translateX(${rocksOffset}px)` }}
            >
                {rocks.map((rock, index) => (
                    <img
                        key={index}
                        // `shade` picks the chip rather than tinting it: the art
                        // already carries its own light-left/dark-right values,
                        // and a brightness filter on top of that flattened them.
                        src={GROUND_CHIP_IMAGES[Math.floor(rock.shade * GROUND_CHIP_IMAGES.length) % GROUND_CHIP_IMAGES.length]}
                        alt=""
                        draggable={false}
                        className="absolute select-none"
                        style={{
                            left: `${rock.x}%`,
                            top: rock.y - groundTop,
                            width: rock.size * CHIP_WIDTH_SCALE,
                            height: 'auto',
                            maxWidth: 'none',
                        }}
                    />
                ))}
            </div>

            {/* DS-29: scattered, zone-aware ground clutter — see surfaceScene.ts's scatterSurfaceProps. */}
            <div
                data-testid="planet-surface-props"
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 bottom-0"
                style={{ top: groundTop, width: WORLD_WIDTH, transform: `translateX(${rocksOffset}px)` }}
            >
                {surfaceProps.map((prop, index) => {
                    const src = SURFACE_PROP_ASSETS[effectiveRace][prop.kind];
                    if (!src) return null;
                    const height = PROP_TARGET_HEIGHT_PX * prop.scale * propDepthScale(prop.y, groundTop, groundHeight);
                    return (
                        <div
                            key={index}
                            className="absolute"
                            style={{ left: `${prop.x}%`, top: prop.y - groundTop, transform: 'translateX(-50%)' }}
                        >
                            {/*
                              DS-29 issue #227: props had no contact shadow.
                              Round 2 ("shadows are too far away at the base,
                              they must follow building shadow patterns"):
                              the original version anchored via `bottom: -4`
                              *plus* a further `translateY(50%)`, which pushed
                              the whole ellipse well below the sprite's own
                              visual base rather than hugging it. Anchored
                              flush to the contact line now (`bottom-0`, no
                              extra downward shift), and skewed to lean right
                              — the same light-from-left/shadow-falls-right
                              direction `buildingShadow.ts`'s
                              `CAST_SHADOW_SKEW_DEG` establishes for real
                              buildings, so props read as lit the same way.
                              Rendered before the sprite so it paints behind
                              it by DOM order, sized off the prop's own
                              height since its real width isn't known
                              synchronously (unlike BuildingTile, not worth
                              per-image measurement for small decorative
                              clutter).
                            */}
                            <div
                                aria-hidden="true"
                                className="absolute bottom-0 left-1/2"
                                style={{
                                    width: height * 1.05,
                                    height: height * 0.3,
                                    transform: `translateX(calc(-50% + ${height * 0.14}px)) skewX(-22deg)`,
                                    transformOrigin: 'bottom center',
                                    borderRadius: '50%',
                                    background: 'radial-gradient(ellipse at 50% 50%, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 70%)',
                                    filter: 'blur(2px)',
                                }}
                            />
                            <img
                                src={src}
                                alt=""
                                data-prop-kind={prop.kind}
                                draggable={false}
                                className="absolute bottom-0 left-1/2 select-none"
                                style={{
                                    height,
                                    width: 'auto',
                                    // Tailwind's preflight resets every `<img>` to
                                    // `max-width: 100%` — harmless against a normally-sized
                                    // ancestor, but this prop's own wrapper div is
                                    // deliberately zero-width (a pure anchor point), so that
                                    // 100% resolved to 0px and silently collapsed every prop
                                    // to nothing. Overridden inline since inline style always
                                    // wins over Tailwind's base-layer rule.
                                    maxWidth: 'none',
                                    transform: prop.flip ? 'translateX(-50%) scaleX(-1)' : 'translateX(-50%)',
                                }}
                            />
                        </div>
                    );
                })}
            </div>

            <div
                data-testid="planet-surface-tiles"
                className="absolute inset-x-0 bottom-0"
                style={{ top: groundTop, width: WORLD_WIDTH, transform: `translateX(${rocksOffset}px)` }}
            >
                {SURFACE_LAYOUT.map((slot) => {
                    const projection = projectTile(slot.depth, groundTop, groundHeight);
                    // `projectTile` returns a stage-space Y, but this layer is
                    // already offset to `groundTop` — subtract it back out or
                    // every tile lands a full groundTop below the stage.
                    const tileStyle = {
                        left: slot.worldX,
                        top: projection.top - groundTop,
                        zIndex: projection.zIndex,
                        opacity: projection.opacity,
                        transform: `translate(-50%, -100%) scale(${projection.scale})`,
                    };

                    // DS-28: which building (if any) sits on this plot is
                    // read live from `planet.buildings[type].plotId` — the
                    // plot itself no longer decides that in advance.
                    const occupant = planet ? findPlotOccupant(planet, slot.id) : undefined;

                    if (!occupant) {
                        // An empty plot still shows its own construct's
                        // progress, so queuing one reads on the surface
                        // instead of leaving the plot looking untouched
                        // until it completes.
                        const activeConstruct = pendingBuildsByPlot[slot.id]?.[0];
                        const upgrade: BuildingUpgradeProgress | undefined =
                            activeConstruct && activeConstruct.eventDetails.startDate.getTime() <= now
                                ? {
                                    value: 1 - (activeConstruct.endDate.getTime() - now) / (activeConstruct.eventDetails.durationSeconds * 1_000),
                                    remainingMs: activeConstruct.endDate.getTime() - now,
                                }
                                : undefined;
                        const construct = activeConstruct
                            ? {
                                buildingType: activeConstruct.eventDetails.buildingType,
                                race: view.buildingRace(activeConstruct.eventDetails.buildingType) ?? race,
                                queued: !upgrade,
                            }
                            : undefined;
                        return <PlotTile key={slot.id} id={slot.id} zone={slot.zone} style={tileStyle} upgrade={upgrade} construct={construct} />;
                    }

                    const level = planet?.buildings?.[occupant]?.level ?? 0;
                    const activeUpgrade = pendingBuildsByBuilding[occupant]?.[0];
                    const upgrade: BuildingUpgradeProgress | undefined =
                        activeUpgrade && activeUpgrade.eventDetails.startDate.getTime() <= now
                            ? {
                                value: 1 - (activeUpgrade.endDate.getTime() - now) / (activeUpgrade.eventDetails.durationSeconds * 1_000),
                                remainingMs: activeUpgrade.endDate.getTime() - now,
                            }
                            : undefined;

                    return (
                        <BuildingTile
                            key={slot.id}
                            id={slot.id}
                            buildingType={occupant}
                            zone={slot.zone}
                            level={level}
                            race={view.buildingRace(occupant) ?? race}
                            style={tileStyle}
                            upgrade={upgrade}
                        />
                    );
                })}
            </div>

            <div
                data-testid="planet-surface-zone-edges"
                className="pointer-events-none absolute inset-y-0"
                style={{ width: WORLD_WIDTH, transform: `translateX(${zoneEdgeOffset}px)` }}
            >
                {ZONE_BOUNDARIES.map((boundaryX, index) => {
                    const zone = ZONE_ORDER[index + 1] as BuildingZone;
                    return (
                        <div key={boundaryX} data-testid={`zone-edge-${boundaryX}`} className="absolute top-0 bottom-0" style={{ left: boundaryX }}>
                            <div
                                className={cn('absolute inset-y-0 w-px opacity-60', ZONE_BG_CLASS[zone])}
                                style={{ left: 0 }}
                            />
                            <div
                                className={cn('absolute top-0 h-3 w-3 -translate-x-1/2 rounded-full blur-[2px]', ZONE_BG_CLASS[zone])}
                            />
                        </div>
                    );
                })}
            </div>

            <div
                data-testid="planet-surface-vignette-left"
                className="pointer-events-none absolute inset-y-0 left-0 w-[120px]"
                style={{ background: 'linear-gradient(90deg, rgba(5,5,15,.6), rgba(5,5,15,0))' }}
            />
            <div
                data-testid="planet-surface-vignette-right"
                className="pointer-events-none absolute inset-y-0 right-0 w-[120px]"
                style={{ background: 'linear-gradient(270deg, rgba(5,5,15,.6), rgba(5,5,15,0))' }}
            />

            <div
                data-testid="planet-surface-zone-wash"
                className="pointer-events-none absolute inset-0"
                style={{ background: ZONE_WASH_COLOR[activeZone], transition: 'background 500ms ease' }}
            />

            <SurfaceListViewLink activeZone={activeZone} />

            <ZoneStrip
                scrollX={scrollX}
                viewportWidth={viewportWidth}
                activeZoneIndex={activeZoneIndex}
                onScrollTo={scrollTo}
                dockHeight={dockHeight}
            />

            {openSlot && planet && anchorRect && stageRef.current && (
                <BuildingPopover
                    open
                    slot={openSlot}
                    anchorRect={anchorRect}
                    bounds={stageRef.current.getBoundingClientRect()}
                    planet={planet}
                    race={race}
                    scienceLevels={scienceLevels}
                    projectedLevels={projectedLevels}
                    pendingBuildsByBuilding={pendingBuildsByBuilding}
                    pendingConstructsByPlot={pendingBuildsByPlot}
                    queueFull={queueFull}
                    options={buildOptions}
                    now={now}
                    onClose={() => setOpenTileId(null)}
                />
            )}
        </div>
    );
}

export default PlanetSurface;
